import React, { useEffect, useState } from 'react';
import { eligibilityRulesTicgnAPI } from '../lib/api';
import { Search, Plus, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AdminCodesNAFTICGN = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'energy_manager';
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    naf_code_label: '',
    libelle: '',
    segment: '',
    eligibility_type: 'NON_CONCERNE',
    eligibility_reason: ''
  });

  const ELIGIBILITE_POTENTIELLE = [
    { value: 'EXONERATION', label: 'Exoneration totale', color: 'bg-[#10D183]/10 text-[#10D183] border-[#10D183]/20' },
    { value: 'TARIF_REDUIT_SANS_RATIO', label: 'Tarif reduit (non soumis au ratio)', color: 'bg-[#397DFF]/10 text-[#397DFF] border-[#397DFF]/20' },
    { value: 'TARIF_REDUIT_AVEC_RATIO', label: 'Tarif reduit (soumis au ratio >= 0,5% VAC)', color: 'bg-[#FF6B35]/10 text-[#FF6B35] border-[#FF6B35]/20' },
    { value: 'NON_CONCERNE', label: 'Non concerne', color: 'bg-[#687175]/10 text-[#687175] border-[#687175]/20' }
  ];

  const MOTIFS_EXONERATION_USAGE = [
    'Usage autre que carburant ou combustible',
    'Doubles usages (reductions chimique, electrolyse, procedes mineralogiques)',
    'Fabrication de produits mineraux non metalliques',
    'Produits consommes pour la production de produits energetiques et assimiles',
    'Production d\'electricite et maintien de la capacite de production',
    'Biogaz combustible non injecte dans le reseau',
    'Navigation interieure a des fins commerciales ou autorites publiques',
    'Navigation maritime a des fins commerciales ou autorites publiques',
    'Navigation aerienne pour prestations de services et autorites publiques'
  ];

  const MOTIFS_TARIF_REDUIT_SANS_RATIO = [
    'Usage pour carburant (depuis le 01/01/2024)',
    'Travaux agricoles et forestiers (depuis le 01/01/2026)'
  ];

  const MOTIFS_TARIF_REDUIT_AVEC_RATIO = [
    'Installation intensive soumise au systeme d\'echange de quotas CO2',
    'Installation intensive soumise a la concurrence internationale',
    'Deshydratation de legumes et plantes aromatiques'
  ];

  const getMotifs = () => {
    if (formData.eligibility_type === 'EXONERATION') return MOTIFS_EXONERATION_USAGE;
    if (formData.eligibility_type === 'TARIF_REDUIT_SANS_RATIO') return MOTIFS_TARIF_REDUIT_SANS_RATIO;
    if (formData.eligibility_type === 'TARIF_REDUIT_AVEC_RATIO') return MOTIFS_TARIF_REDUIT_AVEC_RATIO;
    return [];
  };

  useEffect(() => { fetchCodes(); }, []);

  const fetchCodes = async (searchTerm = '') => {
    try {
      const { data } = await eligibilityRulesTicgnAPI.list(searchTerm);
      setCodes(data || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => { e.preventDefault(); fetchCodes(search); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await eligibilityRulesTicgnAPI.update(editingId, formData);
      } else {
        await eligibilityRulesTicgnAPI.create(formData);
      }
      setShowModal(false);
      resetForm();
      fetchCodes();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette regle TICGN ?')) return;
    try {
      await eligibilityRulesTicgnAPI.delete(id);
      fetchCodes();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur');
    }
  };

  const openEditModal = (code) => {
    setEditingId(code.id);
    setFormData({
      naf_code_label: code.naf_code_label,
      libelle: code.libelle,
      segment: code.segment || '',
      eligibility_type: code.eligibility_type,
      eligibility_reason: code.eligibility_reason
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ naf_code_label: '', libelle: '', segment: '', eligibility_type: 'NON_CONCERNE', eligibility_reason: '' });
    setEditingId(null);
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div></div>;

  return (
    <div data-testid="admin-codes-naf-ticgn-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-[#191D1E]">Codes NAF - TICGN</h1>
          <p className="text-[#687175] mt-1 text-sm">Motifs d'eligibilite et d'exoneration specifiques au gaz naturel</p>
        </div>
        {isAdmin && (
          <button onClick={() => { setShowModal(true); resetForm(); }} className="flex items-center gap-2 bg-[#FF6B35] hover:bg-[#e55e2d] text-white font-medium py-2.5 px-5 rounded-lg" data-testid="add-naf-ticgn-btn">
            <Plus className="w-4 h-4" /> Nouveau code NAF TICGN
          </button>
        )}
      </div>

      <div className="bg-white border border-[#BFC6CC] rounded-lg p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#687175]" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par code NAF ou libelle..." className="w-full pl-11 pr-4 py-2.5 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#FF6B35] text-sm" />
          </div>
          <button type="submit" className="px-5 py-2.5 bg-[#FF6B35] hover:bg-[#e55e2d] text-white font-medium rounded-lg text-sm">Rechercher</button>
        </form>
      </div>

      {codes.length === 0 ? (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-12 text-center">
          <p className="text-[#687175] mb-2">Aucun code NAF TICGN configure</p>
          <p className="text-sm text-[#687175]">Les regles TICFE seront utilisees par defaut. Ajoutez des regles specifiques TICGN ici.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#BFC6CC] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F5F6F9] border-b border-[#BFC6CC]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#687175] uppercase">Code NAF / Libelle</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#687175] uppercase">Eligibilite TICGN</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#687175] uppercase">Motif</th>
                {isAdmin && <th className="px-6 py-3 text-right text-xs font-semibold text-[#687175] uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#BFC6CC]">
              {codes.map((code) => (
                <tr key={code.id} className="hover:bg-[#F5F6F9]">
                  <td className="px-6 py-3">
                    <div className="font-mono text-[#191D1E] font-semibold text-sm">{code.naf_code_label}</div>
                    <div className="text-xs text-[#687175] mt-0.5">{code.libelle}</div>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                      code.eligibility_type === 'EXONERATION' ? 'bg-[#10D183]/10 text-[#10D183] border-[#10D183]/20' :
                      code.eligibility_type === 'TARIF_REDUIT_SANS_RATIO' ? 'bg-[#397DFF]/10 text-[#397DFF] border-[#397DFF]/20' :
                      code.eligibility_type === 'TARIF_REDUIT_AVEC_RATIO' ? 'bg-[#FF6B35]/10 text-[#FF6B35] border-[#FF6B35]/20' :
                      code.eligibility_type === 'TARIF_REDUIT' ? 'bg-[#FF6B35]/10 text-[#FF6B35] border-[#FF6B35]/20' :
                      'bg-[#687175]/10 text-[#687175] border-[#687175]/20'
                    }`}>
                      {code.eligibility_type === 'EXONERATION' ? 'Exoneration' :
                       code.eligibility_type === 'TARIF_REDUIT_SANS_RATIO' ? 'Reduit (sans ratio)' :
                       code.eligibility_type === 'TARIF_REDUIT_AVEC_RATIO' ? 'Reduit (avec ratio)' :
                       code.eligibility_type === 'TARIF_REDUIT' ? 'Tarif reduit' :
                       'Non concerne'}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-sm text-[#191D1E]">{code.eligibility_reason || '-'}</td>
                  {isAdmin && (
                    <td className="px-6 py-3 text-right">
                      <button onClick={() => openEditModal(code)} className="text-[#397DFF] hover:text-[#4672B6] mr-3"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(code.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-6 py-3 bg-[#F5F6F9] border-t border-[#BFC6CC] text-sm text-[#687175]">
            {codes.length} code(s) NAF TICGN
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-heading font-semibold text-[#191D1E] mb-6">
              {editingId ? 'Modifier' : 'Nouveau'} Code NAF TICGN
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#191D1E] mb-1">Code NAF *</label>
                  <input type="text" required value={formData.naf_code_label} onChange={(e) => setFormData({...formData, naf_code_label: e.target.value})} className="w-full px-3 py-2.5 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#FF6B35] font-mono text-sm" placeholder="Ex: C2014" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#191D1E] mb-1">Segment</label>
                  <input type="text" value={formData.segment} onChange={(e) => setFormData({...formData, segment: e.target.value})} className="w-full px-3 py-2.5 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#FF6B35] text-sm" placeholder="Ex: Industrie Chimique" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-1">Libelle *</label>
                <input type="text" required value={formData.libelle} onChange={(e) => setFormData({...formData, libelle: e.target.value})} className="w-full px-3 py-2.5 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#FF6B35] text-sm" placeholder="Description de l'activite" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-1">Eligibilite TICGN *</label>
                <div className="space-y-2">
                  {ELIGIBILITE_POTENTIELLE.map((elig) => (
                    <label key={elig.value} className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${formData.eligibility_type === elig.value ? 'border-[#FF6B35] bg-orange-50' : 'border-[#BFC6CC] hover:border-[#FF6B35]'}`}>
                      <input type="radio" name="eligibility_type" value={elig.value} checked={formData.eligibility_type === elig.value} onChange={(e) => setFormData({...formData, eligibility_type: e.target.value})} />
                      <span className="font-medium text-sm text-[#191D1E]">{elig.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-1">Motif *</label>
                <select required value={formData.eligibility_reason} onChange={(e) => setFormData({...formData, eligibility_reason: e.target.value})} className="w-full px-3 py-2.5 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#FF6B35] text-sm">
                  <option value="">Selectionner un motif</option>
                  {getMotifs().map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1 px-5 py-2.5 border border-[#BFC6CC] text-[#687175] font-medium rounded-lg hover:bg-[#F5F6F9]">Annuler</button>
                <button type="submit" className="flex-1 bg-[#FF6B35] hover:bg-[#e55e2d] text-white font-medium py-2.5 px-5 rounded-lg">{editingId ? 'Modifier' : 'Creer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCodesNAFTICGN;
